import { z, ZodSchema, SafeParseReturnType } from "zod";

export type ValidationType = {
  email_invalid?: string;
  required_field?: string;
  required_checkbox?: string;
  telephone_invalid?: string;
  passwords_not_matching?: string;
  password_invalid?: string;
};

// Input fields validation
export const requiredString = (message?: string) => {
  return z
    .string({ required_error: message || "Dieses Feld ist erforderlich" })
    .min(1, {
      message: message,
    });
};
export const requiredNumber = (required?: string, invalid?: string) => {
  return z
    .string({ required_error: required || "Dieses Feld ist erforderlich" })
    .regex(numberRegExp, invalid || "Ungültige Zahl");
};
export const optionalNumber = (invalid?: string) => {
  return z
    .string()
    .regex(numberRegExp, invalid || "Ungültige Zahl")
    .optional();
};
export const requiredDate = (message?: string) => {
  return z.date({ required_error: message || "Dieses Feld ist erforderlich" });
};

const numberRegExp = /^[0-9]+$/;

export const privacy = (message?: string) => {
  return z
    .string({ required_error: message || "Dieses Feld ist erforderlich" })
    .refine((value) => value === "on", {
      message: message || "Dieses Feld ist erforderlich",
    });
};

export const e_mail = (required?: string, invalid?: string) => {
  return z
    .string({ required_error: required || "Dieses Feld ist erforderlich" })
    .email({ message: invalid || "E-Mail ist ungültig" });
};
export const phoneRegExp =
  /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/;

export const phoneNumber = (message?: string) => {
  return z
    .string()
    .regex(phoneRegExp, message || "Telefonnumer ist ungültig")
    .optional();
};
export const requiredPhoneNumber = (required?: string, invalid?: string) => {
  return z
    .string({ required_error: required || "Dieses Feld ist erforderlich" })
    .regex(phoneRegExp, invalid || "Telefonnumer ist ungültig");
};
// password validation: at least 10 characters, 1 special character, 1x capital letter

export const passwordRegExp = /^(?=.*[A-Z])(?=.*[^A-Za-z0-9]).{10,}$/u;
export const password = (required?: string, invalid?: string) => {
  return z
    .string({ required_error: required || "Dieses Feld ist erforderlich" })
    .regex(
      passwordRegExp,
      invalid ||
        "Passwort muss mindestens 10 Zeichen lang sein, eine Zahl, ein Sonderzeichen und einen Großbuchstaben enthalten.",
    );
};

// email schema
export const emailSchema = (message: ValidationType) => {
  return z.object({
    email: e_mail(message?.required_field, message?.email_invalid),
  });
};

export const DeleteCodeSchema = (message: ValidationType) => {
  return z.object({
    code: requiredString(message?.required_field),
  });
};

export const LoginSchema = (message: ValidationType) => {
  return z.object({
    email: e_mail(
      message?.required_field?.replace("dieses Feld", "E-Mail"),
      message?.email_invalid,
    ),
    password: requiredString(
      message?.required_field?.replace("dieses Feld", "Passwort"),
    ),
  });
};

export const userSchema = (message: ValidationType) => {
  return z
    .object({
      firstName: requiredString(
        message?.required_field?.replace("dieses Feld", "Vorname"),
      ),
      lastName: requiredString(
        message?.required_field?.replace("dieses Feld", "Nachname"),
      ),
      email: e_mail(
        message?.required_field?.replace("dieses Feld", "E-Mail"),
        message?.email_invalid,
      ),
      password: password(
        message?.required_field?.replace("dieses Feld", "Passwort"),
        message?.password_invalid,
      ),
      repeat: requiredString(
        message?.required_field?.replace("dieses Feld", "Passwort wiederholen"),
      ),
      policy: privacy(message?.required_checkbox),
    })
    .refine((data) => data.password === data.repeat, {
      message: message?.passwords_not_matching,
      path: ["repeat"],
    });
};
export const userUpdateSchema = (message: ValidationType) => {
  return z.object({
    firstName: requiredString(message?.required_field),
    lastName: requiredString(message?.required_field),
  });
};
export const passwordResetSchema = (message: ValidationType) => {
  {
    /* jscpd:ignore-start  */
  }
  return z
    .object({
      password: password(
        message?.required_field?.replace("dieses Feld", "Passwort"),
        message?.password_invalid,
      ),
      repeat: requiredString(
        message?.required_field?.replace("dieses Feld", "Passwort wiederholen"),
      ),
    })
    .refine((data) => data.password === data.repeat, {
      message: message?.passwords_not_matching,
      path: ["repeat"],
    });
  {
    /* jscpd:ignore-end  */
  }
};
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const safeParseSchema = <T>(schema: ZodSchema, data: any) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const result: SafeParseReturnType<any, T> = schema.safeParse(data);

  if (!result.success) {
    return {
      flattenedErrors: result.error.flatten().fieldErrors,
    };
  } else {
    return { data: result.data, flattenedErrors: {} };
  }
};
